/**
 * TimelineDOMTransitions — the item/DOM halves of the home timeline's
 * work-transition resets (back-from-work and deeplink).
 *
 * The webgl contract calls STAY in HomeTimeline's resetFromWork /
 * resetForDeeplink facades; this class owns the item resets, the y-offset
 * tweens on the overlaid UI refs and the _domRefs storage those tweens read
 * (HomeTimeline.setUIRefs threads the refs through — no other timeline
 * code reads them). Bodies are a verbatim extraction from HomeTimeline;
 * the item registry is sampled via deps (storage stays timeline-side).
 */
import type { TimelineDOMTransitionsDeps } from 'app/views/home/timeline/timeline.types';
import { TimelineItem } from 'app/views/home/timeline/TimelineItem';

import { DepartmentType } from 'app/data/Enums';
import { device } from 'utils/device';

export class TimelineDOMTransitions {
	private _deps: TimelineDOMTransitionsDeps;
	private _domRefs: any;

	constructor(deps: TimelineDOMTransitionsDeps) {
		this._deps = deps;
	}

	/** Store the CORE UI refs for elements overlaid over the home screen: */
	public setUIRefs(
		$intro: HTMLElement,
		$screens: HTMLElement,
		$spaces: HTMLElement,
		$systems: HTMLElement,
		$workContainerHTML: HTMLElement
	): void {
		this._domRefs = {
			screens: $screens,
			spaces: $spaces,
			intro: $intro,
			systems: $systems,
			workContainer: $workContainerHTML
		};
	}

	/** Item/DOM half of the deeplink reset (the facade adds the webgl half after). */
	public resetForDeeplink(): void {
		//Loop through home items at reset:
		this._deps.getItems().forEach((item: TimelineItem) => {
			item.reset();
		});

		//Reset the y position of the spaces / screens UI elements:
		gsap.TweenMax.set([this._domRefs.screens, this._domRefs.spaces], { y: 0 });
	}

	/**
	 * Item/DOM half of the back-from-work reset — returns the shared offY so
	 * the facade can pass it to the webgl half after.
	 */
	public resetFromWork(department: DepartmentType): number {
		let offY: number = this._getWorkOffY();

		//Loop through home items at reset
		this._deps.getItems().forEach((item: TimelineItem) => {
			//If active department then reset in place for the animation in
			//Otherwise just reset as standard tile
			if (item.department === department) {
				item.resetFromWork(offY);
			} else {
				item.reset();
			}
		});

		//Put the HTML ui element for the department off-screen:
		let $ui: HTMLElement = this._domRefs[department];
		gsap.TweenMax.set($ui, { y: offY });

		return offY;
	}

	/** Get work off Y for when animating items off screen to work */
	private _getWorkOffY(): number {
		return -1 * device.innerHeight;
	}
}
